import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import extraClassesService from '../../services/extraClassesService';
import DialogModal from '../../components/DialogModal';

function ParentExtraClassesSection({ selectedChild }) {
  const [classes, setClasses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const loadData = async () => {
    try {
      const [classesData, enrollmentsData] = await Promise.all([
        extraClassesService.listAll(),
        extraClassesService.getParentEnrollments(),
      ]);
      setClasses(classesData.extra_classes || []);
      setEnrollments(enrollmentsData.enrollments || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load extra classes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [selectedChild]);

  const handleRegister = async (extraClassId) => {
    try {
      await extraClassesService.enroll(extraClassId, selectedChild?.id);
      toast.success('Registration request sent');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Unable to register');
    }
  };

  const handleWithdraw = (extraClassId, enrollmentId, childName) => {
    setConfirmDialog({
      title: 'Withdraw From Class',
      message: `Withdraw ${childName || 'this child'} from this class? Future billing cycles will be cancelled.`,
      type: 'warning',
      confirmLabel: 'Withdraw',
      onConfirm: async () => {
        try {
          setWithdrawingId(enrollmentId);
          await extraClassesService.withdrawEnrollment(extraClassId, enrollmentId);
          toast.success('Withdrawn — you will not be billed for future cycles');
          loadData();
        } catch (err) {
          toast.error(err.message || 'Unable to withdraw');
        } finally {
          setWithdrawingId(null);
        }
      },
    });
  };

  const handlePayCycle = async (extraClassId, billingCycleId) => {
    try {
      setPayingId(billingCycleId);
      const response = await extraClassesService.payBillingCycle(extraClassId, billingCycleId);
      if (response.success && response.payment_url) {
        window.open(response.payment_url, '_blank', 'width=800,height=700');
        toast.success('Payment checkout opened');
      } else {
        toast.error(response.error || 'Failed to initialize payment');
      }
    } catch (err) {
      toast.error(err.message || 'Unable to process payment');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16 }}>
        <h3>Virtual Extra Classes</h3>
        {loading ? <p>Loading...</p> : classes.length === 0 ? <p>No classes available right now.</p> : classes.map((item) => (
          <div key={item.id} style={{ border: '1px solid #e5e7eb', padding: 12, borderRadius: 8, marginBottom: 8 }}>
            <strong>{item.title}</strong>
            <div>{item.description}</div>
            <div>Price: GHS {item.price} ({item.pricing_type})</div>
            <div>Schedule: {item.schedule || 'To be announced'}</div>
            <div>Contact: {item.contact_phone || item.contact_email}</div>
            <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>These are optional teacher-led extra lessons for students who need extra support in a subject.</div>
            <button onClick={() => handleRegister(item.id)} style={{ marginTop: 8 }}>Register {selectedChild?.name || 'child'}</button>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h3>My Registrations</h3>
        {enrollments.length === 0 ? <p>No registrations yet.</p> : enrollments.map((item) => {
          const progress = item.progress || {};
          const summary = progress.summary || {};
          const attendance = progress.attendance || {};
          const assignmentPreview = progress.assignments ? progress.assignments.slice(0, 3) : [];

          return (
            <div key={item.id} style={{ border: '1px solid #e5e7eb', padding: 12, borderRadius: 8, marginBottom: 12, background: '#fff' }}>
              <strong>{item.extra_class?.title}</strong>
              <div>
                Status: <span style={{ textTransform: 'capitalize' }}>{item.waitlisted ? 'Waitlisted' : item.status}</span>
                {item.waitlisted && (
                  <span style={{ marginLeft: 6, fontSize: 12, color: '#92400e' }}>— this class is full, you'll be approved automatically if a spot opens up</span>
                )}
              </div>
              <div>Billing cycle: <span style={{ textTransform: 'capitalize' }}>{item.extra_class?.billing_interval || 'monthly'}</span></div>
              <div>Teacher payout: <span style={{ textTransform: 'capitalize' }}>{item.extra_class?.payout_frequency || 'monthly'}</span></div>
              <div>Amount due: GHS {item.billing_amount || item.extra_class?.price || 0}</div>
              <div>Next due: {item.next_due_date || 'Pending'}</div>

              {progress && (summary.total_assignments || attendance.sessions_held) ? (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
                    <div>
                      <strong>{summary.total_assignments ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Assignments</div>
                    </div>
                    <div>
                      <strong>{summary.graded ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Graded</div>
                    </div>
                    <div>
                      <strong>{summary.submitted ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Submitted</div>
                    </div>
                    <div>
                      <strong>{summary.pending ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Pending</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                    <div>
                      <strong>{summary.overdue ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Overdue</div>
                    </div>
                    <div>
                      <strong>{summary.late ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Late</div>
                    </div>
                    <div>
                      <strong>{summary.average_score ? `${summary.average_score}%` : '—'}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Avg. score</div>
                    </div>
                    <div>
                      <strong>{attendance.sessions_held ?? 0}</strong>
                      <div style={{ fontSize: 12, color: '#475569' }}>Sessions held</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#475569' }}><strong>Avg attendance:</strong> {attendance.average_attendance ?? 0}</div>
                    <div style={{ fontSize: 12, color: '#475569' }}><strong>Last session:</strong> {attendance.last_session_date || 'n/a'}</div>
                    <div style={{ fontSize: 12, color: '#475569' }}><strong>Next session:</strong> {attendance.next_session_date || 'n/a'}</div>
                  </div>
                </div>
              ) : null}

              {assignmentPreview.length > 0 && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Recent assignment progress</div>
                  {assignmentPreview.map((assignment) => (
                    <div key={assignment.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{assignment.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{assignment.due_date ? new Date(assignment.due_date).toLocaleDateString('en-GB') : 'No due date'}</div>
                      </div>
                      <div style={{ fontSize: 12, textTransform: 'capitalize', color: '#334155' }}>{assignment.status}</div>
                    </div>
                  ))}
                  {progress.assignments && progress.assignments.length > 3 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>{progress.assignments.length - 3} more assignments</div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => handlePayCycle(item.extra_class?.id, item.billing_cycle_id)}
                  disabled={!item.billing_cycle_id || item.billing_status === 'paid' || payingId === item.billing_cycle_id}
                >
                  {payingId === item.billing_cycle_id
                    ? 'Opening checkout...'
                    : item.billing_status === 'paid'
                      ? 'Paid'
                      : item.billing_cycle_id
                        ? 'Pay current cycle'
                        : 'Billing pending'}
                </button>
                {(item.status === 'pending' || item.status === 'approved' || item.status === 'active') && (
                  <button
                    onClick={() => handleWithdraw(item.extra_class?.id, item.id, selectedChild?.name)}
                    disabled={withdrawingId === item.id}
                    style={{ background: 'transparent', color: '#b91c1c', border: '1px solid #fecaca' }}
                  >
                    {withdrawingId === item.id ? 'Withdrawing...' : 'Withdraw'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default ParentExtraClassesSection;
